/**
 * הודעת משוב מקובעת בתחתית המסך (שלב 6ד, A-97) — **לא בזרימת הדף**, ולכן אינה
 * מזיזה את הכרטיס שהבקר עובד עליו. הצלחה נעלמת לבד אחרי 3 שניות; שגיאה נשארת
 * עד סגירה. `role="status"` + `aria-live="polite"` — נגישה גם לקורא מסך; שגיאה —
 * `role="alert"` (6ו).
 *
 * 6ו (A-156): נכנסת מלמטה ונעלמת באותו מסלול, 160ms, ב־CSS בלבד (`toast-pre` /
 * `toast-out`); עם prefers-reduced-motion — fade בלבד. מוצגת מעל הסרגל התחתון.
 *
 * 6ו-ב חלק 3 (A-187): ה־`.toast-area` מוצבת ב־portal ל־`div.toast-root` (App.tsx) — אח של
 * עטיפת התוכן, מחוץ לאלמנט שמותמר כשחלונית פתוחה (`transform` על אב הופך `position: fixed`
 * ליחסי אליו). בלי יעד (בדיקות יחידה, לפני שהשלד הוצב) — מרונדרת במקומה, אותו markup.
 */

import { createContext, useContext, useEffect, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';

/** האלמנט שאליו ההודעות מוצבות (`div.toast-root` ב־App.tsx); null — במקום. */
export const ToastSlotContext = createContext<HTMLElement | null>(null);

export type ToastMessage = { ok: boolean; text: string } | null;

const OK_MS = 3_000;
/** משך תנועת היציאה — כמו ב־CSS (`.toast`). */
const OUT_MS = 160;

export function Toast({
  message,
  onClose,
}: {
  message: ToastMessage;
  onClose: () => void;
}): ReactElement | null {
  // כל הודעה חדשה מאפסת את השעון — גם אם הטקסט זהה.
  const [shown, setShown] = useState<ToastMessage>(null);
  const [phase, setPhase] = useState<'pre' | 'in' | 'out'>('in');
  useEffect(() => {
    if (message === null) {
      // יציאה: התנועה, ואז ההודעה מוסרת.
      setPhase('out');
      const t = setTimeout(() => setShown(null), OUT_MS);
      return () => clearTimeout(t);
    }
    setShown(message);
    setPhase('pre');
    // שתי מסגרות: הדפדפן מצייר את מצב ההתחלה ואז עובר למצב הסופי (transition).
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setPhase('in')));
    const t = message.ok ? setTimeout(onClose, OK_MS) : undefined;
    return () => {
      cancelAnimationFrame(raf);
      if (t !== undefined) clearTimeout(t);
    };
  }, [message, onClose]);
  const motion = phase === 'pre' ? ' toast-pre' : phase === 'out' ? ' toast-out' : '';
  const slot = useContext(ToastSlotContext);
  const area = (
    <div className="toast-area" role="status" aria-live="polite" data-testid="toast-area">
      {shown !== null && (
        <div
          className={`toast ${shown.ok ? 'toast-ok' : 'toast-error'}${motion}`}
          role={shown.ok ? undefined : 'alert'}
          data-testid="toast"
        >
          <span className="toast-text">{shown.text}</span>
          <button
            type="button"
            className="toast-close"
            onClick={onClose}
            aria-label="סגירת ההודעה"
            data-testid="toast-close"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
  return slot === null ? area : createPortal(area, slot);
}
